<template>
    <b-modal
        id="terms-dialog"
        v-model="showDialog"
        size="xl"
        :title="termsTitle"
        scrollable
        header-bg-variant="primary"
        header-text-variant="light"
        body-class="terms-body"
        footer-class="terms-footer"
    >
        <div class="terms-content">
            <b-alert show variant="info" class="mb-4" v-if="termsAlert">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>{{termsAlert}}</strong>
            </b-alert>

            <template v-for="(paragraph, index) in termsParagraphs">
                <p v-if="paragraph.heading" :key="'h-' + index"><strong>{{paragraph.heading}}</strong></p>
                <p v-else :key="'p-' + index" v-html="renderParagraph(paragraph)"></p>
            </template>
        </div>

        <template #modal-footer="{ close }">
            <b-button variant="primary" @click="close()">
                Close
            </b-button>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'TermsDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        plan: {
            type: Object,
            default: () => ({})
        },
        formData: {
            type: Object,
            default: () => ({})
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        showDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        termsDocument() {
            return this.config.termsDocument || {};
        },
        termsTitle() {
            return this.termsDocument.title || this.config.title || 'Agreement Terms';
        },
        termsAlert() {
            return this.termsDocument.alert || '';
        },
        termsParagraphs() {
            return this.termsDocument.paragraphs || [];
        },
        // Build the variables map for interpolation
        // Uses same formatting as PDF form fill (buildPdfFormData) for consistency
        templateVars() {
            const formatCurrency = (value) => {
                if (!value) return '$0.00';
                return '$' + parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            };
            const formatDate = (dateStr) => {
                if (!dateStr) return '';
                // Parse date-only strings (YYYY-MM-DD) without timezone shift
                const parts = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (parts) {
                    const date = new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
                    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
                }
                const date = new Date(dateStr);
                return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            };

            const fullName = (this.formData.first_name && this.formData.last_name)
                ? `${this.formData.first_name} ${this.formData.last_name}`
                : '__________________________________________';

            const monthlyPayment = this.plan.monthly
                ? formatCurrency(this.plan.monthly)
                : '____________';

            // Use agreement_fields values (same source as PDF fill)
            const clockHours = this.plan.clock_hours || this.plan.hours || '________';

            const courseExpiration = this.plan.expiration_date
                ? formatDate(this.plan.expiration_date)
                : '_________________________';

            const firstPaymentDate = this.plan.defer_on
                ? formatDate(this.plan.defer_on)
                : '_________________________';

            const termMonths = this.plan.term || '_______';

            const totalAmount = this.plan.agreement_total
                ? formatCurrency(this.plan.agreement_total)
                : '______________';

            const taxAmount = this.plan.tax_amount
                ? formatCurrency(this.plan.tax_amount)
                : '______________';

            const paCost = this.plan.pa_cost
                ? formatCurrency(this.plan.pa_cost)
                : '______________';

            const agreementNumber = this.plan.agreement_number || '______________';

            const taxIncluded = this.plan.included_sales_tax || '$0.00';

            const salesTax = this.plan.sales_tax || 'N/A';

            return {
                fullName,
                monthlyPayment,
                clockHours,
                courseExpiration,
                firstPaymentDate,
                termMonths,
                totalAmount,
                taxAmount,
                salesTax,
                paCost,
                agreementNumber,
                taxIncluded
            };
        }
    },
    methods: {
        escapeHtml(str) {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        },
        renderParagraph(paragraph) {
            let html = '';
            if (paragraph.bold) {
                html += `<strong>${this.escapeHtml(paragraph.bold)}</strong> `;
            }
            if (paragraph.text) {
                // Replace {variableName} with highlighted values
                html += paragraph.text.replace(/\{(\w+)\}/g, (match, key) => {
                    const value = this.templateVars[key];
                    if (value === undefined) return match;
                    return `<strong class="highlight-data">${this.escapeHtml(String(value))}</strong>`;
                });
            }
            return html;
        }
    }
};
</script>

<style lang="scss" scoped>
    .terms-content {
        h5 {
            color: #464577;
            margin-bottom: 20px;
            padding-bottom: 15px;
            border-bottom: 2px solid #e9ecef;
        }

        h6 {
            color: #464577;
            margin-top: 25px;
            margin-bottom: 12px;
            font-weight: 600;
        }

        p {
            line-height: 1.6;
            margin-bottom: 15px;
            text-align: justify;
        }

        ::v-deep .highlight-data {
            color: #464577;
            background-color: #fff3cd;
            padding: 2px 6px;
            border-radius: 3px;
            font-weight: 700;
        }
    }

    ::v-deep .terms-body {
        max-height: 70vh;
        padding: 20px 30px;

        @media (max-width: 768px) {
            padding: 15px 20px;
            max-height: 60vh;
        }
    }

    ::v-deep .modal-header {
        background-color: #464577 !important;

        .modal-title {
            font-size: 1.25rem;
            font-weight: 600;

            @media (max-width: 768px) {
                font-size: 1.1rem;
            }
        }
    }

    ::v-deep .terms-footer {
        padding: 15px 20px;
        border-top: 2px solid #e9ecef;
    }

    ::v-deep .modal-xl {
        max-width: 1000px;

        @media (max-width: 768px) {
            max-width: 95%;
            margin: 10px auto;
        }
    }
</style>
